import { useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { Search, ArrowRight } from "lucide-react";

export const RuleTable = ({ rules }) => {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [selectedRule, setSelectedRule] = useState(null);
  const filtered = useMemo(() => {
    return rules.filter((r) => {
      if (filter !== "all" && r.status !== filter) return false;
      if (!q) return true;
      const t = q.toLowerCase();
      const svc = r.services || `${r.protocol || ""}/${r.port || ""}`;
      return (
        (r.rule_id || "").toLowerCase().includes(t) ||
        (r.name || "").toLowerCase().includes(t) ||
        (r.source || "").toLowerCase().includes(t) ||
        (r.destination || "").toLowerCase().includes(t) ||
        svc.toLowerCase().includes(t) ||
        (r.action || "").toLowerCase().includes(t)
      );
    });
  }, [rules, q, filter]);

  const actionColor = (a) => {
    const v = a?.toUpperCase();
    if (v === "ALLOW" || v === "PERMIT" || v === "ACCEPT") return "text-emerald-400";
    if (v === "DENY" || v === "DROP" || v === "REJECT" || v === "BLOCK")
      return "text-red-400";
    return "text-slate-300";
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input
            data-testid="rule-search-input"
            placeholder="Search by rule id, name, ip, service…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 bg-[#020617] border-[#1e293b] rounded-sm font-mono text-sm placeholder:text-slate-600"
          />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger
            data-testid="rule-filter-select"
            className="w-full sm:w-52 bg-[#020617] border-[#1e293b] rounded-sm font-mono text-sm"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#0f172a] border-[#1e293b] rounded-sm">
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="shadowed">Shadowed only</SelectItem>
            <SelectItem value="redundant">Redundant only</SelectItem>
            <SelectItem value="overlap">Overlap only</SelectItem>
            <SelectItem value="clean">Clean only</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="border border-[#1e293b] rounded-sm overflow-hidden">
        <div className="overflow-x-auto max-h-[560px]">
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-[#0f172a]">
              <TableRow className="border-[#1e293b] hover:bg-transparent">
                {["#", "Rule ID", "Name", "Action", "Source", "Destination", "Services", "Status", "Type", "Covered By", "Explanation"].map(
                  (h) => (
                    <TableHead
                      key={h}
                      className="text-[10px] tracking-[0.2em] uppercase text-slate-400 font-mono"
                    >
                      {h}
                    </TableHead>
                  )
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow className="border-[#1e293b]">
                  <TableCell
                    colSpan={11}
                    className="text-center py-10 text-slate-500 font-mono text-sm"
                  >
                    No rules match the current filters.
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((r) => (
                <TableRow
                  key={`${r.index}-${r.rule_id}`}
                  data-testid={`rule-row-${r.rule_id}`}
                  className="border-[#1e293b] hover:bg-[#0f172a]/60"
                  style={{
                    transition: "background-color 0.15s ease",
                    cursor: "pointer"
                  }}
                  onClick={() => setSelectedRule(r)}
                >
                  <TableCell className="font-mono text-xs text-slate-500">
                    {r.index + 1}
                  </TableCell>
                  <TableCell className="font-mono text-sm text-slate-100">
                    {r.rule_id}
                  </TableCell>
                  <TableCell className="text-sm text-slate-200">{r.name}</TableCell>
                  <TableCell
                    className={`font-mono text-xs font-semibold ${actionColor(r.action)}`}
                  >
                    {r.action?.toUpperCase()}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-slate-300 whitespace-nowrap max-w-[220px] truncate" title={r.source}>
                    {r.source}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-slate-300 whitespace-nowrap max-w-[220px] truncate" title={r.destination}>
                    {r.destination}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-slate-300 whitespace-nowrap max-w-[220px] truncate" title={r.services || `${r.protocol}/${r.port}`}>
                    {r.services || `${r.protocol}/${r.port}`}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={r.status} />
                  </TableCell>

                  <TableCell className="font-mono text-xs">
                    {r.redundancy_type || "-"}
                  </TableCell>

                  

                  <TableCell className="font-mono text-xs text-blue-400">
                    {r.conflicts_with || "-"}
                  </TableCell>
                  <TableCell className="text-xs text-slate-400 leading-relaxed min-w-[280px]">
                    {r.conflicts_with ? (
                      <div className="flex items-start gap-2">
                        <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-slate-500 mt-0.5 shrink-0">
                          #{r.conflicts_with}
                        </span>
                        <ArrowRight className="h-3 w-3 mt-1 text-slate-600 shrink-0" />
                        <span>{r.explanation}</span>
                      </div>
                    ) : (
                      <span>{r.explanation}</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <div className="mt-2 text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">
        showing {filtered.length} of {rules.length} rules
      </div>
      {selectedRule && (

      <div className="rule-drawer">

       <div className="flex justify-between items-center mb-4">

        <h3 className="text-lg font-semibold text-slate-100">
          Rule Relationship
        </h3>
        <div
          className="
            mt-3
            mb-4
            rounded-sm
            border
            border-[#1e293b]
            p-3
          "
        >

          <div className="text-xs uppercase tracking-wider text-slate-400">
            Status
          </div>

          <div className="mt-1 text-lg font-semibold">

            {selectedRule.status?.toUpperCase()}

          </div>

          <div className="text-sm text-slate-400">

            {selectedRule.redundancy_type || "No redundancy detected"}

          </div>

        </div>
        <button
          onClick={() => setSelectedRule(null)}
          className="text-slate-500 hover:text-white"
        >
          ✕
        </button>

      </div>

      <div className="space-y-4">

        <div>
          <div className="text-slate-400">Rule ID</div>
          <div className="font-mono">
            {selectedRule.rule_id}
          </div>
        </div>

        <div>
          <div className="text-slate-400">Status</div>
          <div>{selectedRule.status}</div>
        </div>

        <div>
          <div className="text-slate-400">Redundancy Type</div>
          <div>{selectedRule.redundancy_type || "N/A"}</div>
        </div>

        <div>
          <div className="text-slate-400">Covered By</div>
          <div className="text-blue-400 font-mono">
            {selectedRule.conflicts_with || "N/A"}
          </div>
        </div>
        <div className="border border-[#1e293b] rounded-sm p-3">

        <h4 className="text-sm font-semibold text-slate-200 mb-2">
          Relationship
        </h4>

        <div className="font-mono text-xs text-slate-300">

          {selectedRule.conflicts_with
            ? `Rule ${selectedRule.conflicts_with} → Rule ${selectedRule.rule_id}`
            : `Rule ${selectedRule.rule_id}`
          }

        </div>

      </div>

        <div className="border border-[#1e293b] rounded-sm p-3">

          <h4 className="text-sm font-semibold text-slate-200 mb-3">
            Coverage Analysis
          </h4>

          <div className="space-y-3">

            <div className="flex justify-between">
              <span className="text-slate-400">
                Source
              </span>

              <span className="text-emerald-400">
                ✓ Evaluated
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-slate-400">
                Destination
              </span>

              <span className="text-emerald-400">
                ✓ Evaluated
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-slate-400">
                Services
              </span>

              <span className="text-emerald-400">
                ✓ Evaluated
              </span>
            </div>

          </div>

        </div>
        <div className="border border-[#1e293b] rounded-sm p-3">

          <h4 className="text-sm font-semibold text-slate-200 mb-2">
            Cleanup Recommendation
          </h4>

          {selectedRule.redundancy_type === "duplicate" && (
            <div className="text-red-400">
              Delete Rule
            </div>
          )}

          {selectedRule.redundancy_type === "subset" && (
            <div className="text-amber-400">
              Remove After Validation
            </div>
          )}

          {selectedRule.redundancy_type === "covered_by_global_rule" && (
            <div className="text-yellow-400">
              Review Before Removal
            </div>
          )}

          {!selectedRule.redundancy_type && (
            <div className="text-emerald-400">
              Keep Rule
            </div>
          )}

        </div>





        <div>
          <div className="text-slate-400">Source</div>
          <div className="font-mono text-xs break-all text-slate-300">
            {selectedRule.source}
          </div>
        </div>

        <div>
          <div className="text-slate-400">Destination</div>
          
          <div className="font-mono text-xs break-all text-slate-300">
            {selectedRule.destination}
          </div>
        </div>

        <div>
          <div className="text-slate-400">Services</div>

          <div className="font-mono text-xs break-all text-slate-300">
            {selectedRule.services}
          </div>
        </div>

        <div>
          <div className="text-slate-400">Explanation</div>
   
          <div className="font-mono text-xs break-all text-slate-300">
            {selectedRule.explanation}
          </div>
        </div>

      </div>

    </div>

  )}  
    </div>


);
};
